import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { blogPosts, blogThumbnails } from "@/data/blogData";
import { Search } from "lucide-react";

const categories = ["All", "Awareness", "Consideration", "Decision"];

const Blog = () => {
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = blogPosts.filter((p) => {
    if (cat !== "All" && p.category.toLowerCase() !== cat.toLowerCase()) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <Layout>
      <SEOHead title="AI Automation Blog | Xentic Solutions" description="Learn how AI chatbots and automation can transform your small business. Tips, guides, and real case studies." path="/blog" />

      <section className="pt-24 pb-20">
        <div className="container">
          {/* Header */}
          <ScrollReveal>
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Our Blog</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">Insights & Guides</h1>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Practical tips on AI automation, lead generation, and growing your service business.
              </p>
            </div>
          </ScrollReveal>

          {/* Search */}
          <ScrollReveal delay={0.05}>
            <div className="relative max-w-md mx-auto mb-8">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                aria-label="Search articles"
                placeholder="Search articles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-secondary border-border"
              />
            </div>
          </ScrollReveal>

          {/* Category filters */}
          <ScrollReveal delay={0.08}>
            <div className="flex justify-center gap-2 mb-12 flex-wrap">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    cat === c
                      ? "text-white glow-shadow-sm"
                      : "glass-card text-muted-foreground hover:text-foreground"
                  }`}
                  style={cat === c ? { background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" } : undefined}
                >
                  {c}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((post, i) => (
              <ScrollReveal key={post.slug} delay={i * 0.05}>
                <Link to={`/blog/${post.slug}`}>
                  <Card className="h-full overflow-hidden group">
                    {blogThumbnails[post.slug] && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={blogThumbnails[post.slug]}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <CardContent className="p-5 md:p-6">
                      <Badge variant="secondary" className="mb-3 capitalize text-[11px]">{post.category}</Badge>
                      <h2 className="font-heading font-bold text-base md:text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed mb-3">{post.hook}</p>
                      <span className="text-sm text-primary font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <span className="transition-transform group-hover:translate-x-0.5">→</span>
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-lg">No articles found matching your search.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
